import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Heart, Briefcase, Sparkles, Sun, Moon, User, Compass, Eye, FileText, Check, Clock, Shield, Target, Users, Lightbulb, BookOpen, Gem, TrendingUp, AlertTriangle, Zap, Layers, Calendar } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
import { ServicePurchaseSection } from '../components/ServicePurchaseSection';
interface ComboAnalysisPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-22868">
      {children}
    </motion.section>;
}
export function ComboAnalysisPage({
  onNavigate
}: ComboAnalysisPageProps) {
  const ctaRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const {
    addItem
  } = useCart();
  const handleBuyPremium = () => {
    addItem({
      id: `premium-combo-${Date.now()}`,
      name: 'Premium комбо: Натал + Соляр',
      description: 'Полный профессиональный отчёт с глубоким анализом натальной карты и соляра, практическими рекомендациями.',
      price: 6900,
      type: 'premium-report',
      serviceName: 'Комбо: Натал + Соляр',
      format: 'PDF-отчёт',
      duration: '50-60 страниц',
      badges: [{
        label: 'Premium',
        variant: 'pro'
      }]
    });
    onNavigate('checkout');
  };
  const scrollToCta = () => {
    ctaRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-22869">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 sm:pt-8 pb-14 sm:pb-24 lg:pb-32" data-id="element-22870">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-violet-600/15 rounded-full blur-3xl pointer-events-none" data-id="element-22871" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-22872" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-22873">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Услуги',
          onClick: () => onNavigate('marketplace')
        }, {
          label: 'Комбо: Натал + Соляр'
        }]} className="mb-6 sm:mb-12 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-22874" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center" data-id="element-22875">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-22876">
              <motion.div variants={staggerItem} className="mb-4 sm:mb-5" data-id="element-22877">
                <Badge variant="pro" className="bg-violet-500/20 text-violet-300 border-violet-500/30 px-3 py-1 text-sm" data-id="element-22878">
                  Выгода 17%
                </Badge>
              </motion.div>

              <motion.h1 variants={staggerItem} className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 leading-[1.15]" data-id="element-22879">
                Полный астрологический анализ вашей жизни и прогноза на год
              </motion.h1>

              <motion.p variants={staggerItem} className="text-sm sm:text-base md:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed" data-id="element-22880">
                Комбинированный отчёт объединяет натальную карту и соляр, чтобы
                показать вашу личность, жизненные задачи и основные события
                ближайшего года.
              </motion.p>

              <motion.div variants={staggerItem} className="mb-6 sm:mb-10" data-id="element-22881">
                <Button size="lg" onClick={scrollToCta} className="text-base sm:text-lg px-6 sm:px-8 shadow-lg shadow-violet-500/25 bg-violet-600 hover:bg-violet-700" data-id="element-22882">
                  Получить полный анализ
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-22883" />
                </Button>
              </motion.div>

              <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-22884">
                {[{
                icon: <User className="w-4 h-4" data-id="element-22885" />,
                text: 'Анализ вашей личности'
              }, {
                icon: <Sparkles className="w-4 h-4" data-id="element-22886" />,
                text: 'Раскрытие талантов и предназначения'
              }, {
                icon: <Calendar className="w-4 h-4" data-id="element-22887" />,
                text: 'Прогноз событий на год'
              }, {
                icon: <Target className="w-4 h-4" data-id="element-22888" />,
                text: 'Рекомендации для решений'
              }].map((item, i) => <div key={i} className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-22889">
                    <div className="w-7 h-7 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-300 flex-shrink-0" data-id="element-22890">
                      {item.icon}
                    </div>
                    {item.text}
                  </div>)}
              </motion.div>
            </motion.div>

            {/* Illustration — two overlapping circles */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.2
          }} className="hidden lg:flex justify-center relative" data-id="element-22891">
              <div className="relative w-[420px] h-[420px]" data-id="element-22892">
                {/* Natal circle */}
                <div className="absolute left-0 top-[10%] w-[280px] h-[280px] rounded-full border border-indigo-500/25 flex items-center justify-center" data-id="element-22893">
                  <div className="w-40 h-40 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full blur-3xl opacity-20" data-id="element-22894" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-22895">
                    <Moon className="w-14 h-14 text-indigo-300/60" strokeWidth={1} data-id="element-22896" />
                  </div>
                  <div className="absolute inset-0 rounded-full border border-indigo-400/10 animate-[spin_50s_linear_infinite]" data-id="element-22897" />
                </div>
                {/* Solar circle */}
                <div className="absolute right-0 bottom-[10%] w-[280px] h-[280px] rounded-full border border-amber-500/25 flex items-center justify-center" data-id="element-22898">
                  <div className="w-40 h-40 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full blur-3xl opacity-20" data-id="element-22899" />
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-22900">
                    <Sun className="w-14 h-14 text-amber-300/60" strokeWidth={1} data-id="element-22901" />
                  </div>
                  <div className="absolute inset-0 rounded-full border border-amber-400/10 animate-[spin_40s_linear_infinite_reverse]" data-id="element-22902" />
                </div>
                {/* Intersection glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-br from-violet-400 to-fuchsia-500 rounded-full blur-2xl opacity-30 animate-pulse" data-id="element-22903" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-22904">
                  <Layers className="w-10 h-10 text-white/70" strokeWidth={1.5} data-id="element-22905" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 2. ЧТО ВХОДИТ В КОМБО-АНАЛИЗ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-22906">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22907">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4 text-center" data-id="element-22908">
            Что входит в комбо-анализ
          </h2>
          <p className="text-base sm:text-lg text-slate-600 text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-22909">
            Отчёт состоит из двух взаимодополняющих частей
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 max-w-5xl mx-auto" data-id="element-22910">
            {/* Natal */}
            <Card padding="lg" className="border-indigo-100 relative overflow-hidden" data-id="element-22911">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-22912" />
              <div className="relative z-10" data-id="element-22913">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-6" data-id="element-22914">
                  <Moon className="w-7 h-7" data-id="element-22915" />
                </div>
                <Badge variant="neutral" className="mb-4 bg-indigo-50 text-indigo-700 border-indigo-100" data-id="element-22916">
                  Часть 1
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-22917">
                  Натальная карта
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-22918">
                  Натальная карта показывает особенности вашей личности, сильные
                  стороны, таланты, жизненные задачи и потенциал развития. Это
                  фундамент, на котором строится понимание вашей природы.
                </p>
              </div>
            </Card>

            {/* Solar */}
            <Card padding="lg" className="border-amber-100 relative overflow-hidden" data-id="element-22919">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-50 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" data-id="element-22920" />
              <div className="relative z-10" data-id="element-22921">
                <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-6" data-id="element-22922">
                  <Sun className="w-7 h-7" data-id="element-22923" />
                </div>
                <Badge variant="neutral" className="mb-4 bg-amber-50 text-amber-700 border-amber-100" data-id="element-22924">
                  Часть 2
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-22925">
                  Соляр (прогноз на год)
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-22926">
                  Соляр — это прогноз на год, который показывает главные
                  события, возможности и вызовы в период от одного дня рождения
                  до следующего. Это ваша дорожная карта на ближайший год.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. ПОЧЕМУ ЛУЧШЕ ВМЕСТЕ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-22927">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22928">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-22929">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-22930">
              Почему лучше делать анализ вместе
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-22931">
              Два анализа дают более точную и полную картину вашей жизни
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8 max-w-6xl mx-auto" data-id="element-22932">
            {/* Natal column */}
            <Card padding="lg" className="border-indigo-100" data-id="element-22933">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-5" data-id="element-22934">
                <Moon className="w-6 h-6" data-id="element-22935" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-22936">
                Натальная карта показывает
              </h3>
              <ul className="space-y-3" data-id="element-22937">
                {['Кто вы по своей природе', 'Ваши сильные стороны', 'Ваши внутренние мотивации', 'Таланты и предназначение'].map((t, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-22938">
                    <Check className="w-4 h-4 text-indigo-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-22939" />
                    {t}
                  </li>)}
              </ul>
            </Card>

            {/* Combined — center */}
            <Card padding="lg" className="border-violet-200 ring-1 ring-violet-100 shadow-lg shadow-violet-100/50" data-id="element-22940">
              <div className="w-12 h-12 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center mb-5" data-id="element-22941">
                <Layers className="w-6 h-6" data-id="element-22942" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-22943">
                Вместе они дают
              </h3>
              <ul className="space-y-3" data-id="element-22944">
                {['Полное понимание себя и своего года', 'Связь между характером и событиями', 'Точные рекомендации с учётом личности', 'Максимально полный анализ'].map((t, i) => <li key={i} className="flex items-start text-sm text-slate-700 font-medium" data-id="element-22945">
                    <Sparkles className="w-4 h-4 text-violet-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-22946" />
                    {t}
                  </li>)}
              </ul>
            </Card>

            {/* Solar column */}
            <Card padding="lg" className="border-amber-100" data-id="element-22947">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-5" data-id="element-22948">
                <Sun className="w-6 h-6" data-id="element-22949" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-22950">
                Соляр показывает
              </h3>
              <ul className="space-y-3" data-id="element-22951">
                {['Какие события ждут в этом году', 'Где будут возможности', 'Какие темы будут важнейшими', 'Периоды роста и осторожности'].map((t, i) => <li key={i} className="flex items-start text-sm text-slate-600" data-id="element-22952">
                    <Check className="w-4 h-4 text-amber-500 mr-2.5 mt-0.5 flex-shrink-0" data-id="element-22953" />
                    {t}
                  </li>)}
              </ul>
            </Card>
          </div>
        </div>
      </AnimatedSection>

      {/* 4. ЧТО ВЫ УЗНАЕТЕ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-22954">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22955">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-22956">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-22957">
              Что вы узнаете из отчёта
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-22958">
              Шесть ключевых направлений анализа вашей жизни
            </p>
          </div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-22959">
            {[{
            icon: <User className="w-6 h-6" data-id="element-22960" />,
            color: 'bg-indigo-50 text-indigo-600',
            title: 'Личность и характер',
            desc: 'Глубокий анализ психологических особенностей, темперамента и способа восприятия мира.'
          }, {
            icon: <Sparkles className="w-6 h-6" data-id="element-22961" />,
            color: 'bg-violet-50 text-violet-600',
            title: 'Таланты и предназначение',
            desc: 'Какие способности заложены в вашей карте и как их раскрыть в полной мере.'
          }, {
            icon: <Heart className="w-6 h-6" data-id="element-22962" />,
            color: 'bg-rose-50 text-rose-600',
            title: 'Отношения и любовь',
            desc: 'Как вы строите отношения, какие партнёры вам подходят и что мешает гармонии.'
          }, {
            icon: <Briefcase className="w-6 h-6" data-id="element-22963" />,
            color: 'bg-emerald-50 text-emerald-600',
            title: 'Карьера и деньги',
            desc: 'Какие направления деятельности наиболее благоприятны и где искать финансовый успех.'
          }, {
            icon: <Star className="w-6 h-6" data-id="element-22964" />,
            color: 'bg-amber-50 text-amber-600',
            title: 'Главные темы года',
            desc: 'Какие события могут быть наиболее важными в течение ближайшего года жизни.'
          }, {
            icon: <AlertTriangle className="w-6 h-6" data-id="element-22965" />,
            color: 'bg-orange-50 text-orange-600',
            title: 'Возможности и вызовы',
            desc: 'Периоды роста и возможные трудности — когда действовать, а когда выжидать.'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} data-id="element-22966">
                <Card padding="lg" className="h-full border-slate-100" data-id="element-22967">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${item.color} flex items-center justify-center mb-4 sm:mb-5`} data-id="element-22968">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-22969">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-22970">
                    {item.desc}
                  </p>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* 5. СТРУКТУРА ПОЛНОГО ОТЧЁТА */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-22971">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22972">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-22973">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-22974">
              Структура полного отчёта
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-22975">
              Более 50 страниц персонального анализа в двух частях
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 max-w-5xl mx-auto" data-id="element-22976">
            {/* Part 1 — Natal */}
            <div data-id="element-22977">
              <div className="flex items-center gap-3 mb-6" data-id="element-22978">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center" data-id="element-22979">
                  <Moon className="w-5 h-5" data-id="element-22980" />
                </div>
                <h3 className="text-xl font-bold text-slate-900" data-id="element-22981">
                  Часть 1 — Натальная карта
                </h3>
              </div>
              <div className="space-y-3" data-id="element-22982">
                {[{
                icon: <Sun className="w-4 h-4" data-id="element-22983" />,
                text: 'Анализ Солнца, Луны и Асцендента'
              }, {
                icon: <Star className="w-4 h-4" data-id="element-22984" />,
                text: 'Планеты в знаках зодиака'
              }, {
                icon: <BookOpen className="w-4 h-4" data-id="element-22985" />,
                text: 'Планеты в домах гороскопа'
              }, {
                icon: <Gem className="w-4 h-4" data-id="element-22986" />,
                text: 'Аспекты между планетами'
              }, {
                icon: <Sparkles className="w-4 h-4" data-id="element-22987" />,
                text: 'Таланты и предназначение'
              }, {
                icon: <User className="w-4 h-4" data-id="element-22988" />,
                text: 'Психологический профиль'
              }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-5 py-3.5 border border-slate-100" data-id="element-22989">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-22990">
                      {item.icon}
                    </div>
                    <span className="text-sm font-medium text-slate-700" data-id="element-22991">
                      {item.text}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Part 2 — Solar */}
            <div data-id="element-22992">
              <div className="flex items-center gap-3 mb-6" data-id="element-22993">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-22994">
                  <Sun className="w-5 h-5" data-id="element-22995" />
                </div>
                <h3 className="text-xl font-bold text-slate-900" data-id="element-22996">
                  Часть 2 — Соляр
                </h3>
              </div>
              <div className="space-y-3" data-id="element-22997">
                {[{
                icon: <Star className="w-4 h-4" data-id="element-22998" />,
                text: 'Общая тема года'
              }, {
                icon: <BookOpen className="w-4 h-4" data-id="element-22999" />,
                text: 'Активные дома соляра'
              }, {
                icon: <Zap className="w-4 h-4" data-id="element-23000" />,
                text: 'Влияние планет в солярной карте'
              }, {
                icon: <Briefcase className="w-4 h-4" data-id="element-23001" />,
                text: 'Прогноз по основным сферам жизни'
              }, {
                icon: <AlertTriangle className="w-4 h-4" data-id="element-23002" />,
                text: 'Зоны риска и предупреждения'
              }, {
                icon: <Compass className="w-4 h-4" data-id="element-23003" />,
                text: 'Рекомендации на год'
              }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-5 py-3.5 border border-slate-100" data-id="element-23004">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-23005">
                      {item.icon}
                    </div>
                    <span className="text-sm font-medium text-slate-700" data-id="element-23006">
                      {item.text}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 6. ПРИМЕР ОТЧЁТА */}
      <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-23007">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23008">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl" data-id="element-23009">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-23010">
              <div className="p-6 sm:p-10 lg:p-16 flex flex-col justify-center" data-id="element-23011">
                <span className="text-xs font-bold text-violet-300 uppercase tracking-[0.2em] mb-3 sm:mb-4" data-id="element-23012">
                  Пример отчёта
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight" data-id="element-23013">
                  Два профессиональных отчёта в одном документе
                </h2>
                <p className="text-slate-400 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed" data-id="element-23014">
                  Комбо-отчёт содержит графические схемы обеих карт, подробный
                  анализ личности и годовой прогноз с практическими
                  рекомендациями.
                </p>
                <ul className="space-y-3 mb-6 sm:mb-10" data-id="element-23015">
                  {['Графическая схема натальной карты', 'Графическая схема солярной карты', 'Персональный анализ личности', 'Прогноз по сферам жизни на год', 'Более 50 страниц в PDF'].map((t, i) => <li key={i} className="flex items-center text-slate-300 text-sm" data-id="element-23016">
                      <Check className="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" data-id="element-23017" />
                      {t}
                    </li>)}
                </ul>
                <Button size="lg" variant="outline" className="w-fit bg-white border-0 hover:bg-slate-100" onClick={() => onNavigate('report-preview-combo')} data-id="element-23018">
                  Посмотреть пример PDF
                </Button>
              </div>
              <div className="bg-slate-800/50 p-4 sm:p-8 lg:p-12 flex items-center justify-center" data-id="element-23019">
                <div className="relative" data-id="element-23020">
                  {/* Natal report page */}
                  <motion.div animate={{
                  y: [0, -6, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="w-56 bg-white rounded-xl shadow-2xl p-6 relative z-10" data-id="element-23021">
                    <div className="w-full h-20 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg mb-4 flex items-center justify-center" data-id="element-23022">
                      <div className="w-12 h-12 rounded-full border-2 border-indigo-200 flex items-center justify-center" data-id="element-23023">
                        <Moon className="w-5 h-5 text-indigo-300" data-id="element-23024" />
                      </div>
                    </div>
                    <div className="w-3/4 h-2.5 bg-slate-200 rounded mb-2" data-id="element-23025" />
                    <div className="w-1/2 h-2.5 bg-slate-100 rounded mb-4" data-id="element-23026" />
                    <div className="space-y-2" data-id="element-23027">
                      <div className="w-full h-1.5 bg-slate-100 rounded" data-id="element-23028" />
                      <div className="w-full h-1.5 bg-slate-100 rounded" data-id="element-23029" />
                      <div className="w-4/5 h-1.5 bg-slate-50 rounded" data-id="element-23030" />
                    </div>
                  </motion.div>
                  {/* Solar report page — offset */}
                  <motion.div animate={{
                  y: [0, -4, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: 0.5
                }} className="w-56 bg-white rounded-xl shadow-xl p-6 absolute top-12 left-16 z-20" data-id="element-23031">
                    <div className="w-full h-20 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg mb-4 flex items-center justify-center" data-id="element-23032">
                      <div className="w-12 h-12 rounded-full border-2 border-amber-200 flex items-center justify-center" data-id="element-23033">
                        <Sun className="w-5 h-5 text-amber-400" data-id="element-23034" />
                      </div>
                    </div>
                    <div className="w-3/4 h-2.5 bg-slate-200 rounded mb-2" data-id="element-23035" />
                    <div className="w-1/2 h-2.5 bg-slate-100 rounded mb-4" data-id="element-23036" />
                    <div className="flex items-end gap-1 h-8 mb-3" data-id="element-23037">
                      {[50, 70, 40, 85, 60, 45, 75, 55].map((h, i) => <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-amber-200 to-amber-100" style={{
                      height: `${h}%`
                    }} data-id="element-23038" />)}
                    </div>
                    <div className="space-y-2" data-id="element-23039">
                      <div className="w-full h-1.5 bg-slate-100 rounded" data-id="element-23040" />
                      <div className="w-3/4 h-1.5 bg-slate-50 rounded" data-id="element-23041" />
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 7. ДЛЯ КОГО */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23042">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23043">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-23044">
            Для кого подходит этот анализ
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-23045">
            {[{
            icon: <Eye className="w-5 h-5" data-id="element-23046" />,
            text: 'Людям, которые хотят лучше понять себя и свою природу'
          }, {
            icon: <Target className="w-5 h-5" data-id="element-23047" />,
            text: 'Тем, кто планирует важные решения в ближайший год'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-23048" />,
            text: 'Людям, интересующимся астрологией и самопознанием'
          }, {
            icon: <Calendar className="w-5 h-5" data-id="element-23049" />,
            text: 'Тем, кто хочет знать тенденции ближайшего года'
          }, {
            icon: <Lightbulb className="w-5 h-5" data-id="element-23050" />,
            text: 'Тем, кто ищет своё предназначение и подходящий путь'
          }, {
            icon: <Users className="w-5 h-5" data-id="element-23051" />,
            text: 'Тем, кто хочет подарить близким полезный и уникальный подарок'
          }].map((item, i) => <div key={i} className="flex items-center gap-4 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-23052">
                <div className="w-10 h-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center flex-shrink-0" data-id="element-23053">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-23054">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 8. ПОЧЕМУ САМЫЙ ПОЛНЫЙ */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-23055">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23056">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-23057">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23058">
              Почему этот анализ самый полный
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto" data-id="element-23059">
            {[{
            icon: <User className="w-6 h-6" data-id="element-23060" />,
            color: 'bg-indigo-100 text-indigo-600',
            title: 'Анализ личности',
            desc: 'Полный разбор характера, талантов, мотиваций и психологических особенностей по натальной карте.'
          }, {
            icon: <Compass className="w-6 h-6" data-id="element-23061" />,
            color: 'bg-violet-100 text-violet-600',
            title: 'Жизненный потенциал',
            desc: 'Понимание кармических задач, предназначения и ключевых направлений развития.'
          }, {
            icon: <TrendingUp className="w-6 h-6" data-id="element-23062" />,
            color: 'bg-amber-100 text-amber-600',
            title: 'Прогноз на год',
            desc: 'Детальный прогноз событий, возможностей и вызовов на ближайший год по солярной карте.'
          }].map((item, i) => <Card key={i} padding="lg" className="border-slate-100 text-center" data-id="element-23063">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${item.color} flex items-center justify-center mx-auto mb-4 sm:mb-5`} data-id="element-23064">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-23065">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-23066">
                  {item.desc}
                </p>
              </Card>)}
          </div>
          <p className="text-center text-slate-600 mt-10 max-w-2xl mx-auto" data-id="element-23067">
            Это позволяет получить максимально полное представление о своей
            жизни, возможностях и направлении движения.
          </p>
        </div>
      </AnimatedSection>

      {/* 9. CTA */}
      <div ref={ctaRef} className="scroll-mt-10" data-id="element-23068">
        <AnimatedSection className="py-14 sm:py-24 bg-slate-900" data-id="element-23069">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-23070">
            <motion.div className="flex items-center justify-center gap-3 mb-4 sm:mb-6" animate={{
            scale: [1, 1.08, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-23071">
              <Moon className="w-7 h-7 text-indigo-400" data-id="element-23072" />
              <Layers className="w-6 h-6 text-violet-400" data-id="element-23073" />
              <Sun className="w-7 h-7 text-amber-400" data-id="element-23074" />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6" data-id="element-23075">
              Получите полный астрологический анализ
            </h2>
            <p className="text-base sm:text-lg text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto" data-id="element-23076">
              Введите дату, время и место рождения, чтобы получить персональный
              отчёт с анализом личности и прогнозом на год.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-23077">
              <Button size="lg" onClick={scrollToPricing} className="text-base sm:text-lg px-10 shadow-lg shadow-violet-500/25 bg-violet-600 hover:bg-violet-700" data-id="element-23078">
                Построить натальную карту и соляр
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-23079" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500" data-id="element-23080">
              <span className="flex items-center" data-id="element-23081">
                <Shield className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-23082" /> Данные
                защищены
              </span>
              <span className="flex items-center" data-id="element-23083">
                <Clock className="w-4 h-4 mr-1.5 text-violet-400" data-id="element-23084" /> Результат
                мгновенно
              </span>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* 10. ТАРИФЫ — replaced with ServicePurchaseSection */}
      <div ref={pricingRef} className="scroll-mt-10" data-id="element-23085">
        <ServicePurchaseSection serviceType="combo" onNavigate={onNavigate} data-id="element-23086" />
      </div>

      {/* FAQ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-23087">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-23088">
          <div className="text-center mb-8 sm:mb-12" data-id="element-23089">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-23090">
              Частые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Чем комбо-отчёт отличается от покупки двух отчётов по отдельности?',
          content: 'Комбо-отчёт стоит дешевле, чем два отдельных отчёта. Кроме того, в Premium версии анализ интегрирован — соляр интерпретируется с учётом вашей натальной карты, что даёт более точные и персонализированные рекомендации.'
        }, {
          id: '2',
          title: 'Нужно ли знать точное время рождения?',
          content: 'Да, для точного расчёта обеих карт необходимо знать время рождения. Оно влияет на положение Асцендента, домов гороскопа и солярной карты. Если время неизвестно, наши астрологи могут провести ректификацию.'
        }, {
          id: '3',
          title: 'Как быстро я получу отчёт?',
          content: 'Мгновенно. Сразу после оплаты система сгенерирует PDF-файл с обеими частями отчёта. Вы сможете скачать его на странице успеха, также он будет отправлен на email.'
        }, {
          id: '4',
          title: 'Когда лучше заказывать комбо-отчёт?',
          content: 'Идеальное время — за 1-2 месяца до дня рождения или сразу после. Так вы получите и анализ личности, и актуальный прогноз на предстоящий год.'
        }, {
          id: '5',
          title: 'Чем Beta комбо отличается от Premium?',
          content: 'Beta содержит краткий анализ ключевых показателей и общий прогноз (1-3 страницы). Premium — это полный профессиональный разбор всех планет, домов, аспектов обеих карт, помесячный прогноз и практические рекомендации (50-60 страниц).'
        }]} data-id="element-23091" />
        </div>
      </AnimatedSection>
    </div>;
}